import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import eyeson, {StreamHelpers, FeatureDetector} from 'eyeson';
import Settings from "./settings/settings";
import classNames from 'classnames'
import {useDispatch} from "react-redux";
import {
  addVideoConferenceChatMessage,
  groupChatAddParticipant, groupChatClearParticipants,
  groupChatRemoveParticipant,
  groupChatSetAccessToken, setEyesonReadyState
} from "../virtual_congress/exhibition_actions";

const Video = ({settings, accessKey, additionalVideoClass, videoConferenceIdentifier}) => {
  const dispatch = useDispatch()
  const videoRef = useRef(null);
  const [showSettings, setShowSettings] = useState(false)
  const [audio, setAudio] = useState(true)
  const [video, setVideo] = useState(true)
  const [screen, setScreen] = useState(false)
  const [localStream, setLocalStream] = useState()
  const [eyesonEventListenerInitialized, setEyesonEventListenerInitialized] = useState(false)
  const canUseScreenCapture = useMemo(() => FeatureDetector.canScreenCapture(), [])
  const canFullscreen = useMemo(() => FeatureDetector.canFullscreen(), [])

  const eyesonEventListener = useCallback(
    (event) => {
      console.log("Eyeson Event", {type: event.type, event})
      if (event.localStream) {
        setLocalStream(event.localStream);
      }
      switch (event.type) {
        case "accept":
          dispatch(setEyesonReadyState(true))
          videoRef.current.srcObject = event.remoteStream;
          videoRef.current.play();
          break;
        case "connection":
          if (event.connectionStatus === 'ready') {
            eyeson.join({audio: audio, video: video});
          }
          break;
        case "presentation_ended":
          eyeson.send({
            type: 'start_stream',
            audio: audio,
            video: video
          });
          setScreen(false);
          break;
        case "add_user":
          dispatch(groupChatAddParticipant(event.user))
          break;
        case "remove_user":
          dispatch(groupChatRemoveParticipant(event.user))
          break;
        case "chat":
          dispatch(addVideoConferenceChatMessage({
            content: event.content,
            user: event.user,
            videoConferenceIdentifier
          }))
          break;
      }
    },
    [setLocalStream, videoRef, audio, video, setScreen, dispatch],
  );

  useEffect(() => {
    return () => {
      dispatch(setEyesonReadyState(false))
      dispatch(groupChatClearParticipants())
    }
  }, [dispatch])

  useEffect(() => {
    if (eyesonEventListener) {
      eyeson.onEvent(eyesonEventListener);

      setEyesonEventListenerInitialized(true)

      return () => {
        eyeson.offEvent(eyesonEventListener);
      }
    }
  }, [eyesonEventListener])

  useEffect(() => {
    if (videoRef?.current && accessKey && eyesonEventListenerInitialized) {
      eyeson.connect(accessKey);

      return () => {
        eyeson.destroy();
      };
    }
  }, [videoRef, accessKey, eyesonEventListenerInitialized])

  const handleSettingChanged = () => {
    eyeson.send({
      type: 'start_stream',
      audio: audio,
      video: video
    });
  }

  const handleToggleAudio = () => {
    if (localStream) {
      const audioEnabled = !audio;
      StreamHelpers.toggleAudio(localStream, audioEnabled);
      setAudio(audioEnabled)
    }
  }

  const toggleVideo = () => {
    const videoEnabled = !video;
    eyeson.send({
      type: 'change_stream',
      stream: localStream,
      video: videoEnabled,
      audio: audio
    });
    setVideo(videoEnabled)
  };

  const toggleScreen = () => {
    if (!screen) {
      eyeson.send({
        type: 'start_screen_capture',
        audio: audio,
        screenStream: null,
        screen: true
      });
      setScreen(true)
    } else {
      eyeson.send({type: 'stop_presenting'});
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current && canFullscreen) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen()
      } else if (videoRef.current.webkitRequestFullscreen){
        videoRef.current.webkitRequestFullscreen()
      } else if (videoRef.current.msRequestFullScreen){
        videoRef.current.msRequestFullScreen()
      }
    }
  };

  return <>
    {settings?.enable_user_settings && <div className="text-right">
      {canUseScreenCapture && settings?.enable_user_screen_share_setting &&
        <button type="button" className={classNames("btn btn-light", {disabled: !localStream})} onClick={toggleScreen}>
          {screen ? <i className="fas fa-ban"/> : <i className="fas fa-desktop"/>}
        </button>}
      {settings?.enable_user_mute_cam_setting &&
        <button type="button" className={classNames("btn btn-light", {disabled: !localStream})} onClick={toggleVideo}>
          {video ? <i className="fas fa-video"/> : <i className="fas fa-video-slash"/>}
        </button>}
      {settings?.enable_user_mute_microphone_setting &&
        <button type="button" className={classNames("btn btn-light", {disabled: !localStream})}
                onClick={handleToggleAudio}>
          {audio ? <i className="fas fa-microphone"/> : <i className="fas fa-microphone-slash"/>}
        </button>}
      {settings?.enable_user_io_settings &&
        <button type="button" className={"btn btn-light"} onClick={() => setShowSettings(value => !value)}>
          <i className="fas fa-cog"/>
        </button>}
      {canFullscreen && settings?.allow_fullscreen &&
        <button type="button" className={classNames("btn btn-light", {disabled: !videoRef.current || !localStream})}
                onClick={toggleFullscreen}>
          <i className="fas fa-expand-wide"/>
        </button>}
    </div>}
    {showSettings && <div class={"mb-4"}>
      <Settings videoRef={videoRef} handleSettingChanged={handleSettingChanged} />
    </div>}
    <div className={"embed-responsive embed-responsive-4by3"}>
      <video className={`eyeson-video ${additionalVideoClass || ""}`} autoPlay={true} ref={videoRef}/>
    </div>
  </>
}

export default Video